import dayjs from 'dayjs';
import 'src/utils/format-date-time';

/**
 * Compose the message a landlord sends to propose a suggested tour slot, e.g.
 * "How about tomorrow, Wed, Aug 20 at 2:00 PM?". The slot is dropped into the
 * composer for the landlord to review and send — it does not book a tour.
 *
 * @param isoStartTime - the slot start time (ISO 8601, timezone-aware).
 * @param timezone - the IANA timezone to render the slot in. Callers resolve this
 *   through getEffectiveTourTimezone (building timezone, else the user's timezone),
 *   so it is normally non-null; a null slips through to the browser timezone only as
 *   an unavoidable last resort when neither is set.
 * @returns the suggestion message text.
 */
export function formatSuggestionMessage(isoStartTime: string, timezone: string | null): string {
    const slot = timezone ? dayjs(isoStartTime).tz(timezone) : dayjs(isoStartTime);
    const now = timezone ? dayjs().tz(timezone) : dayjs();
    const dayDiff = slot.startOf('day').diff(now.startOf('day'), 'day');

    const date = slot.format('ddd, MMM D');

    let dayLabel: string;
    if (dayDiff === 0) {
        dayLabel = `today, ${date}`;
    } else if (dayDiff === 1) {
        dayLabel = `tomorrow, ${date}`;
    } else {
        dayLabel = `on ${slot.format('dddd')}, ${slot.format('MMM D')}`;
    }

    const time = slot.format('h:mm A');

    return `How about ${dayLabel} at ${time}?`;
}
